(() => {
  if (window.g2gCartRewards) return;
  const configNode = document.getElementById('g2g-cart-rewards-config');
  if (!configNode) return;
  let config = JSON.parse(configNode.textContent);
  const root = window.Shopify?.routes?.root || '/';
  const sectionId = () => document.getElementById('mini-cart')?.getAttribute('section') || config.sectionId || 'mini-cart';
  const sectionRequest = () => ({ sections: [sectionId()], sections_url: window.location.pathname });
  const excluded = () => new Set([config.giftId, ...(config.excluded || [])].map(Number));
  const giftLines = cart => cart.items.filter(item => item.properties?._g2g_reward === 'gift');
  const countItems = cart => cart.items.reduce((count, item) => count + (excluded().has(Number(item.product_id)) || item.properties?._g2g_reward === 'gift' ? 0 : item.quantity), 0);
  const state = { cart: config.cart, previous: countItems(config.cart), busy: false, dirty: false };
  window.g2gCartRewards = state;
  const waiters = [];
  const release = () => {
    state.busy = false;
    waiters.splice(0).forEach(resolve => resolve());
    if (state.dirty) { state.dirty = false; schedule(); }
  };
  const request = async (path, body) => {
    const response = await fetch(`${root}${path}`, body ? {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    } : { credentials: 'same-origin', cache: 'no-store' });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || config.error);
    return result;
  };
  const showError = error => {
    const element = document.querySelector('g2g-cart-rewards');
    let alert = element?.querySelector('[role="alert"]');
    if (!alert) {
      alert = document.getElementById('g2g-rewards-error');
      if (!alert) {
        alert = document.createElement('p');
        alert.id = 'g2g-rewards-error';
        alert.setAttribute('role', 'alert');
        const form = [...document.forms].find(form => /\/cart\/?$/.test(new URL(form.action).pathname));
        if (!form) return;
        form.prepend(alert);
      }
    }
    alert.textContent = error.message || config.error;
    alert.hidden = false;
  };
  const publish = (cart, openMiniCart = false) => {
    state.cart = cart;
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart, g2gReward: true } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart, replacementDelay: 0, g2gReward: true } }));
  };
  const giftPlan = cart => {
    const eligible = countItems(cart) >= config.thresholds[2];
    const hasPaidGift = cart.items.some(item => Number(item.product_id) === Number(config.giftId) && item.properties?._g2g_reward !== 'gift');
    let allowance = eligible && !hasPaidGift ? 1 : 0;
    const updates = {};
    for (const item of giftLines(cart)) {
      const quantity = Number(item.product_id) === Number(config.giftId) ? Math.min(item.quantity, allowance) : 0;
      allowance -= quantity;
      if (quantity !== item.quantity) updates[item.key] = quantity;
    }
    return { updates, add: allowance > 0 && config.available && Boolean(config.variantId) };
  };
  const needsSync = cart => {
    const plan = giftPlan(cart);
    return plan.add || Object.keys(plan.updates).length > 0;
  };
  const reconcile = async () => {
    let cart = await request('cart.js');
    let changed = false;
    let plan = giftPlan(cart);
    if (Object.keys(plan.updates).length) {
      cart = await request('cart/update.js', { updates: plan.updates, ...sectionRequest() });
      changed = true;
    }
    try {
      if (giftPlan(cart).add) {
        const added = await request('cart/add.js', { items: [{ id: config.variantId, quantity: 1, properties: { _g2g_reward: 'gift' } }], ...sectionRequest() });
        changed = true;
        cart = await request('cart.js');
        cart.sections = added.sections;
        plan = giftPlan(cart);
        if (Object.keys(plan.updates).length) {
          cart = await request('cart/update.js', { updates: plan.updates, ...sectionRequest() });
        }
        state.justClaimed = giftLines(cart).some(item => Number(item.product_id) === Number(config.giftId));
      }
    } finally {
      state.cart = cart;
      if (changed) {
        // A concurrent merchandise update makes the bundled HTML stale.
        if (state.dirty) delete cart.sections;
        publish(cart);
        if (window.themeVariables?.settings?.pageType === 'cart') {
          state.reloading = true;
          window.location.reload();
        }
      }
    }
    return cart;
  };
  let timer;
  let refreshNeeded = false;
  const schedule = (fresh = false) => {
    refreshNeeded ||= fresh;
    clearTimeout(timer);
    if (state.busy) { state.dirty = true; return; }
    timer = setTimeout(async () => {
      if (state.busy) { state.dirty = true; return; }
      if (!refreshNeeded && !needsSync(state.cart)) return;
      refreshNeeded = false;
      state.busy = true;
      try { await reconcile(); } catch (error) { showError(error); }
      finally { release(); }
    }, 1000);
  };
  document.addEventListener('cart:updated', event => {
    if (event.detail?.cart?.items) state.cart = event.detail.cart;
    if (!event.detail?.g2gReward) schedule(state.busy || !event.detail?.cart?.items);
  });
  document.addEventListener('cart:refresh', event => {
    if (event.detail?.cart?.items) state.cart = event.detail.cart;
    if (!event.detail?.g2gReward) schedule(state.busy || !event.detail?.cart?.items);
  });
  window.addEventListener('pageshow', event => schedule(event.persisted));
  document.addEventListener('shopify:section:load', () => {
    const node = document.getElementById('g2g-cart-rewards-config');
    if (node) {
      config = JSON.parse(node.textContent);
      document.querySelectorAll('g2g-cart-rewards').forEach(element => element.syncImage());
      schedule(true);
    }
  });
  document.addEventListener('cart:rendered', event => {
    if (!state.justClaimed) return;
    const line = event.target.querySelector(`[data-product-id="${config.giftId}"]`);
    if (!line) return;
    state.justClaimed = false;
    line.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    line.animate([
      { opacity: 0, transform: 'translateY(12px) scale(.97)', backgroundColor: '#ffe3ee' },
      { opacity: 1, transform: 'translateY(0) scale(1)', backgroundColor: '#fff5f8', offset: .6 },
      { opacity: 1, transform: 'translateY(0) scale(1)', backgroundColor: 'transparent' }
    ], { duration: 700, easing: 'ease-out' });
  });
  let submitting = false;
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (submitting || !(form instanceof HTMLFormElement) || !/\/cart\/?$/.test(new URL(form.action).pathname)) return;
    if (!giftLines(state.cart).length && !needsSync(state.cart) && !state.busy) return;
    event.preventDefault();
    if (state.checkoutPending) return;
    state.checkoutPending = true;
    while (state.busy) await new Promise(resolve => waiters.push(resolve));
    state.busy = true;
    try {
      await reconcile();
      submitting = true;
      const currentForm = form.isConnected ? form : document.getElementById(form.id);
      const submitter = event.submitter?.isConnected ? event.submitter : currentForm?.querySelector('button[name="checkout"], input[name="checkout"]');
      if (currentForm && !state.reloading) currentForm.requestSubmit(submitter || undefined);
    } catch (error) { showError(error); }
    finally { submitting = false; state.checkoutPending = false; release(); }
  }, true);
  class G2GCartRewards extends HTMLElement {
    connectedCallback() {
      this.fill = this.querySelector('.g2g-rewards__fill');
      this.tiers = [...this.querySelectorAll('[data-threshold]')];
      this.count = Number(this.dataset.count);
      this.from = state.previous;
      this.drawer = this.closest('cart-drawer');
      this.syncImage();
      this.setProgress(this.from);
      this.observer = new MutationObserver(() => this.start());
      if (this.drawer) this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });
      this.start();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
    syncImage() {
      if (!config.imageUrl) return;
      const badge = this.querySelector('.g2g-rewards__gift');
      let image = badge.querySelector('img');
      if (!image) {
        image = document.createElement('img');
        image.alt = '';
        image.width = 56;
        image.height = 56;
        badge.replaceChildren(image);
      }
      image.removeAttribute('srcset');
      image.src = config.imageUrl;
      badge.style.setProperty('--reward-gift-mask', `url(${JSON.stringify(config.imageUrl)})`);
    }
    position(count) {
      const [first, second, third] = config.thresholds;
      if (count <= first) return Math.max(0, count / first) * .2;
      if (count <= second) return .2 + (count - first) / (second - first) * .4;
      return Math.min(1, .6 + (count - second) / (third - second) * .4);
    }
    setProgress(count) {
      this.fill.style.transform = `scaleX(${this.position(count)})`;
      this.tiers.forEach(tier => tier.classList.toggle('is-unlocked', count >= Number(tier.dataset.threshold)));
    }
    start() {
      if (this.started || !this.drawer?.hasAttribute('open')) return;
      this.started = true;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = reduced || this.from === this.count ? 0 : 1200;
      const start = performance.now() + (duration ? 250 : 0);
      const tick = now => {
        if (!this.isConnected) return;
        const progress = duration ? Math.max(0, Math.min(1, (now - start) / duration)) : 1;
        const eased = 1 - Math.pow(1 - progress, 3);
        this.setProgress(this.from + (this.count - this.from) * eased);
        if (progress < 1) { this.frame = requestAnimationFrame(tick); return; }
        state.previous = this.count;
        if (this.from < config.thresholds[2] && this.count >= config.thresholds[2] && !reduced) this.tiers[2].classList.add('is-celebrating');
      };
      this.frame = requestAnimationFrame(tick);
    }
  }
  customElements.define('g2g-cart-rewards', G2GCartRewards);
  schedule();
})();
